import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Info } from 'lucide-react';
import { apiClient } from '../../../lib/api';

export type BranchModalMode = 'create_branch' | 'edit_branch' | 'add_pos' | 'edit_pos';

interface SiatBranchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (payload: any, mode: BranchModalMode, ids: { branchId?: number, posId?: number }) => Promise<void>;
    initialData?: any;
    mode: BranchModalMode;
}

export const SiatBranchModal = ({ isOpen, onClose, onSave, initialData, mode }: SiatBranchModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [posTypes, setPosTypes] = useState<any[]>([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);

    const [formData, setFormData] = useState({
        branchCode: '', branchName: '', address: '',
        phoneCountryCode: '', phoneAreaCode: '', phoneNumber: '',
        pointOfSaleCode: '', siatPointOfSaleTypeId: '', cuis: '', cuisExpirationDate: ''
    });

    useEffect(() => {
        const fetchPosTypes = async () => {
            setIsLoadingTypes(true);
            try {
                const response = await apiClient.get<any>('SiatPointOfSaleType');
                const data = response.data?.data || response.data || [];
                setPosTypes(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error al cargar los tipos de punto de venta", error);
            } finally {
                setIsLoadingTypes(false);
            }
        };

        if (isOpen && (mode === 'create_branch' || mode === 'add_pos' || mode === 'edit_pos')) {
            fetchPosTypes();
        }
    }, [isOpen, mode]);

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setFormData({
                    branchCode: initialData.branchCode?.toString() || '',
                    branchName: initialData.branchName || '',
                    address: initialData.address || '',
                    phoneCountryCode: initialData.phoneCountryCode || '591',
                    phoneAreaCode: initialData.phoneAreaCode || '',
                    phoneNumber: initialData.phoneNumber || '',
                    // Si estamos agregando o editando solo la sucursal, vaciamos los datos del PoS por seguridad
                    pointOfSaleCode: mode === 'add_pos' || mode === 'edit_branch' ? '' : initialData.pointOfSaleCode?.toString() || '',
                    siatPointOfSaleTypeId: mode === 'add_pos' || mode === 'edit_branch' ? '' : initialData.siatPointOfSaleTypeId?.toString() || '',
                    cuis: mode === 'add_pos' || mode === 'edit_branch' ? '' : initialData.cuis || '',
                    cuisExpirationDate: mode === 'add_pos' || mode === 'edit_branch' ? '' : (initialData.cuisExpirationDate ? initialData.cuisExpirationDate.split('T')[0] : '')
                });
            } else {
                setFormData({
                    branchCode: '', branchName: '', address: '',
                    phoneCountryCode: '591', phoneAreaCode: '', phoneNumber: '',
                    pointOfSaleCode: '', siatPointOfSaleTypeId: '', cuis: '', cuisExpirationDate: ''
                });
            }
        }
    }, [isOpen, initialData, mode]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        // Validamos que estos campos específicos solo acepten números
        if (name === 'phoneNumber' || name === 'phoneAreaCode') {
            // Reemplaza todo lo que NO sea un dígito (\D) por vacío ('')
            const onlyNumbers = value.replace(/\D/g, '');
            setFormData({ ...formData, [name]: onlyNumbers });
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        let payload: any = {};

        if (mode === 'edit_branch') {
            payload = {
                branchCode: Number(formData.branchCode),
                branchName: formData.branchName,
                address: formData.address,
                phoneCountryCode: formData.phoneCountryCode,
                phoneAreaCode: formData.phoneAreaCode || null,
                phoneNumber: formData.phoneNumber
            };
        } else if (mode === 'add_pos' || mode === 'edit_pos') {
            payload = {
                pointOfSaleCode: Number(formData.pointOfSaleCode),
                siatPointOfSaleTypeId: Number(formData.siatPointOfSaleTypeId),
                cuis: formData.cuis,
                cuisExpirationDate: formData.cuisExpirationDate
            };
        } else if (mode === 'create_branch') {
            payload = {
                branchCode: Number(formData.branchCode),
                branchName: formData.branchName,
                address: formData.address,
                phoneCountryCode: formData.phoneCountryCode,
                phoneAreaCode: formData.phoneAreaCode || null,
                phoneNumber: formData.phoneNumber,
                pointOfSaleCode: formData.pointOfSaleCode ? Number(formData.pointOfSaleCode) : null,
                siatPointOfSaleTypeId: formData.siatPointOfSaleTypeId ? Number(formData.siatPointOfSaleTypeId) : null,
                cuis: formData.cuis || null,
                cuisExpirationDate: formData.cuisExpirationDate || null
            };
        }

        try {
            await onSave(payload, mode, { branchId: initialData?.siatBranchId, posId: initialData?.siatPointOfSaleId });
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const getTitle = () => {
        if (mode === 'create_branch') return 'Registrar Sucursal y Punto de Venta';
        if (mode === 'edit_branch') return 'Editar Datos de Sucursal';
        if (mode === 'add_pos') return 'Agregar Nuevo Punto de Venta';
        if (mode === 'edit_pos') return 'Editar Punto de Venta';
    };

    const showBranchFields = mode === 'create_branch' || mode === 'edit_branch';
    const showPosFields = mode === 'create_branch' || mode === 'add_pos' || mode === 'edit_pos';
    const showBranchInfoCard = mode === 'add_pos' || mode === 'edit_pos';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
            <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-900">{getTitle()}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <form id="branch-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">

                        {/* SECCIÓN SUCURSAL */}
                        {(showBranchFields || showBranchInfoCard) && (
                            <div className="md:col-span-2">
                                <h4 className="text-sm font-semibold text-blue-900 border-b pb-1 mb-3">Datos de la Sucursal</h4>

                                {showBranchInfoCard ? (
                                    <div className="flex gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                                        <Info className="text-blue-500 shrink-0 mt-0.5" size={20} />
                                        <div>
                                            <p className="text-sm font-semibold text-blue-900">
                                                {formData.branchName} <span className="text-blue-600 font-normal">(Código SIN: {formData.branchCode})</span>
                                            </p>
                                            <p className="text-xs text-blue-700 mt-1">{formData.address}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block mb-1 text-xs font-medium text-gray-700">Cód. Sucursal (SIN)</label>
                                            <input type="number" min="0" name="branchCode" value={formData.branchCode} onChange={handleChange} placeholder="0 = Casa Matriz" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium text-gray-700">Nombre Sucursal</label>
                                            <input type="text" name="branchName" value={formData.branchName} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block mb-1 text-xs font-medium text-gray-700">Dirección Física</label>
                                            <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block mb-1 text-xs font-medium text-gray-700">Teléfono / Celular</label>
                                            <div className="flex gap-2">
                                                {/* Código de país */}
                                                <select
                                                    name="phoneCountryCode"
                                                    value={formData.phoneCountryCode}
                                                    onChange={handleChange}
                                                    className="w-28 px-2 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                                                    required
                                                >
                                                    <option value="591">🇧🇴 +591</option>
                                                    <option value="54">🇦🇷 +54</option>
                                                    <option value="56">🇨🇱 +56</option>
                                                    <option value="51">🇵🇪 +51</option>
                                                    <option value="55">🇧🇷 +55</option>
                                                    <option value="57">🇨🇴 +57</option>
                                                    <option value="52">🇲🇽 +52</option>
                                                    <option value="1">🇺🇸 +1</option>
                                                </select>

                                                {/* Código de área */}
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    name="phoneAreaCode"
                                                    value={formData.phoneAreaCode}
                                                    onChange={handleChange}
                                                    maxLength={5}
                                                    placeholder="Área (Ej: 3)"
                                                    className="w-28 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                                                />

                                                {/* Teléfono */}
                                                <input
                                                    type="tel"
                                                    inputMode="numeric"
                                                    name="phoneNumber"
                                                    value={formData.phoneNumber}
                                                    onChange={handleChange}
                                                    maxLength={12}
                                                    placeholder="Número (Ej: 70000001)"
                                                    className="w-full flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    required
                                                />
                                            </div>
                                        </div>

                                    </div>
                                )}
                            </div>
                        )}

                        {/* SECCIÓN PUNTO DE VENTA */}
                        {showPosFields && (
                            <>
                                <div className="md:col-span-2 mt-4"><h4 className="text-sm font-semibold text-blue-900 border-b pb-1">Conexión SIAT y Punto de Venta</h4></div>

                                <div>
                                    <label className="block mb-1 text-xs font-medium text-gray-700">Cód. Punto de Venta</label>
                                    <input type="number" min="0" name="pointOfSaleCode" value={formData.pointOfSaleCode} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                                </div>
                                <div>
                                    <label className="block mb-1 text-xs font-medium text-gray-700">Tipo de Punto de Venta</label>
                                    <select name="siatPointOfSaleTypeId" value={formData.siatPointOfSaleTypeId} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required disabled={isLoadingTypes}>
                                        <option value="" disabled>{isLoadingTypes ? 'Cargando...' : 'Seleccione un tipo'}</option>
                                        {posTypes.map(type => (
                                            <option key={type.siatPointOfSaleTypeId} value={type.siatPointOfSaleTypeId}>
                                                {type.description}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block mb-1 text-xs font-medium text-gray-700">Código CUIS</label>
                                    <input type="text" name="cuis" value={formData.cuis} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono uppercase" required />
                                </div>
                                <div>
                                    <label className="block mb-1 text-xs font-medium text-gray-700">Vencimiento CUIS</label>
                                    <input type="date" name="cuisExpirationDate" value={formData.cuisExpirationDate} onChange={handleChange} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" required />
                                </div>
                            </>
                        )}

                    </form>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                        Cancelar
                    </button>
                    <button type="submit" form="branch-form" disabled={isLoading} className="flex items-center justify-center min-w-[120px] px-4 py-2 text-sm font-medium text-white bg-[#1e3a8a] rounded-lg hover:bg-blue-900 disabled:opacity-70">
                        {isLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                        {mode === 'create_branch' ? 'Registrar' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
};